import {Button, CounterLabel, Spinner} from '@primer/react'
import {AutomatedReviewDiffEntry} from './AutomatedReviewDiffEntry'
import React, {useState, useRef, useCallback} from 'react'
import {ApplySuggestionDialog} from '../CodeSuggestionActions/ApplySuggestionDialog'
import {useApplyAutomatedSuggestionMutation} from '@github-ui/pull-requests/use-apply-automated-suggestion-mutation'
import type {AutomatedReviewSuggestionProps} from './AutomatedReviewSuggestion'
import {FoldIcon, UnfoldIcon} from '@primer/octicons-react'
import type {Suggestion} from '../../../conversation-types'
import styles from './AutomatedReviewSuggestionStates.module.css'
import {AutomatedReviewFeedback} from './AutomatedReviewFeedback'

interface AutomatedReviewSuggestionBaseProps {
  suggestion: Suggestion
  actions?: React.ReactNode
}

function AutomatedReviewSuggestionBase({suggestion, actions}: AutomatedReviewSuggestionBaseProps) {
  const diffEntries = suggestion.diffEntries || []
  const suggestedChangesCount = diffEntries.length || 0

  return (
    <div
      className={`d-flex flex-column color-fg-default ${styles.suggestion}`}
      data-testid="automated-review-suggestion"
    >
      {/* Metadata */}
      {suggestedChangesCount > 0 && (
        <div className="d-flex flex-items-center flex-justify-between text-small color-fg-muted pt-2">
          <div>
            Suggested changeset <CounterLabel scheme="secondary">{suggestedChangesCount}</CounterLabel>
          </div>
          <span> Copilot uses AI. Check for mistakes.</span>
        </div>
      )}

      {/* Diffs */}
      {diffEntries.map(diffEntry => {
        return <AutomatedReviewDiffEntry key={diffEntry.path} diffEntry={diffEntry} />
      })}

      {actions}
    </div>
  )
}

export function AutomatedReviewSuggestionPending(_props: AutomatedReviewSuggestionProps) {
  return (
    <div className={styles.suggestionPending} data-testid="automated-review-suggestion-pending">
      <Spinner size="medium" />
      Loading suggestion...
    </div>
  )
}

// TODO(@aliceclv): we store the suggestion_error_message in the DB, we can display it here
export function AutomatedReviewSuggestionError(_props: AutomatedReviewSuggestionProps) {
  return (
    <div className={styles.suggestionError} data-testid="automated-review-suggestion-error">
      <h3 className={styles.suggestionErrorTitle}>Sorry, we could not generate an autofix suggestion.</h3>
      <p className="color-fg-muted">
        Try pushing a new commit or if the problem persists{' '}
        <a className="fgColor-link" href="https://support.github.com/">
          <u>contact support</u>
        </a>
        .
      </p>
    </div>
  )
}

export function AutomatedReviewSuggestionOutdated({
  suggestion,
  feedbackOptions,
  automatedCommentId,
  basePath,
}: AutomatedReviewSuggestionProps) {
  const Actions = (
    <>
      <div className="d-flex flex-items-center flex-justify-between">
        <span className="color-fg-muted text-small">Unable to commit as this autofix suggestion is now outdated.</span>

        {/* Commit suggestion button is disabled */}
        <Button size="small" disabled>
          Commit suggestion
        </Button>
      </div>
      {/* Feedback buttons */}
      {feedbackOptions && (
        <AutomatedReviewFeedback
          automatedCommentId={automatedCommentId}
          basePath={basePath}
          feedbackOptions={feedbackOptions}
        />
      )}
    </>
  )
  return suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} actions={Actions} />
}

export function AutomatedReviewSuggestionApplied({
  suggestion,
  automatedCommentId,
  feedbackOptions,
  basePath,
}: AutomatedReviewSuggestionProps) {
  const [isFolded, setIsFolded] = useState(true)
  const fold = () => setIsFolded(!isFolded)

  const FeedbackAction = (
    <>
      {/* Feedback buttons */}
      {feedbackOptions && (
        <AutomatedReviewFeedback
          automatedCommentId={automatedCommentId}
          basePath={basePath}
          feedbackOptions={feedbackOptions}
        />
      )}
    </>
  )

  return (
    <div className={styles.suggestionApplied}>
      <div className="d-flex flex-justify-between flex-items-center">
        <span className={styles.suggestionAppliedText}>This autofix suggestion was applied.</span>
        <Button
          variant="invisible"
          onClick={fold}
          leadingVisual={isFolded ? UnfoldIcon : FoldIcon}
          size="small"
          className="color-fg-muted"
        >
          {isFolded ? 'Show' : 'Hide'} autofix suggestion
        </Button>
      </div>
      {!isFolded && suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} actions={FeedbackAction} />}
    </div>
  )
}

export function AutomatedReviewSuggestionPresent({
  automatedCommentId,
  basePath,
  commentAuthorLogin,
  commentTitle,
  feedbackOptions,
  suggestion,
}: AutomatedReviewSuggestionProps) {
  const [isApplySuggestionDialogOpen, setIsApplySuggestionDialogOpen] = useState(false)

  const diffEntries = suggestion?.diffEntries || []
  const suggestedChangesCount = diffEntries.length || 0
  const buttonRef = useRef<HTMLButtonElement>(null)
  const buttonLabel = 'Commit suggestion'
  // Note(@aliceclv): this is optimized for Universe ship & code quality
  // We should have a default commit message passed from the backend when adapting the component to code scanning & CCR
  const defaultCommitMessage = `Potential fix for pull request finding${commentTitle ? ` '${commentTitle}'` : ''}`
  const {mutate: applyAutomatedSuggestion} = useApplyAutomatedSuggestionMutation(automatedCommentId, basePath)

  const onApplySuggestionDialogClose = useCallback(() => setIsApplySuggestionDialogOpen(false), [])
  const handleCommitSuggestion = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      applyAutomatedSuggestion(
        {
          message: commitMessage,
        },
        {
          onSuccess: () => {
            onApplySuggestionDialogClose()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'applyAutomatedSuggestion', 'Failed to commit suggested changes')
          },
        },
      )
    },
    [applyAutomatedSuggestion, onApplySuggestionDialogClose],
  )

  const Actions = (
    <>
      <div className="d-flex flex-items-center flex-justify-between">
        {/* Feedback buttons */}
        {feedbackOptions && (
          <AutomatedReviewFeedback
            automatedCommentId={automatedCommentId}
            basePath={basePath}
            feedbackOptions={feedbackOptions}
          />
        )}

        {/* Commit suggestion */}
        <Button
          data-testid="commit-suggestion-button"
          size="small"
          variant="primary"
          ref={buttonRef}
          onClick={() => setIsApplySuggestionDialogOpen(!isApplySuggestionDialogOpen)}
        >
          {buttonLabel}
        </Button>
      </div>

      {/* Commit suggestion dialog */}
      {isApplySuggestionDialogOpen && suggestedChangesCount > 0 && (
        <ApplySuggestionDialog
          authorLogins={commentAuthorLogin ? [commentAuthorLogin] : []}
          batchSize={suggestedChangesCount}
          defaultCommitMessage={defaultCommitMessage}
          onClose={onApplySuggestionDialogClose}
          onCommit={handleCommitSuggestion}
          dialogTitle={buttonLabel}
        />
      )}
    </>
  )

  return suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} actions={Actions} />
}

try{ AutomatedReviewSuggestionBase.displayName ||= 'AutomatedReviewSuggestionBase' } catch {}
try{ AutomatedReviewSuggestionPending.displayName ||= 'AutomatedReviewSuggestionPending' } catch {}
try{ AutomatedReviewSuggestionError.displayName ||= 'AutomatedReviewSuggestionError' } catch {}
try{ AutomatedReviewSuggestionOutdated.displayName ||= 'AutomatedReviewSuggestionOutdated' } catch {}
try{ Actions.displayName ||= 'Actions' } catch {}
try{ AutomatedReviewSuggestionApplied.displayName ||= 'AutomatedReviewSuggestionApplied' } catch {}
try{ FeedbackAction.displayName ||= 'FeedbackAction' } catch {}
try{ AutomatedReviewSuggestionPresent.displayName ||= 'AutomatedReviewSuggestionPresent' } catch {}
try{ Actions.displayName ||= 'Actions' } catch {}